import { Await, useNavigate, useOutletContext, useAsyncValue, useAsyncError } from "react-router";
import { Suspense, useState, FormEvent } from "react";
import { useQueryState } from "nuqs";
import { href } from "../../lib/utils";
import { newAccount } from "./Login";
import { startRegistration } from "../../lib/xmpp/factories";
import type { RegisterClient, RegisterForm } from "../../lib/xmpp";

const RegisterInner = () => {
	const navigate = useNavigate();
	const { setAccounts, persistence, idbp } = useOutletContext<any>();
	const [register, form] = useAsyncValue() as [RegisterClient, RegisterForm];
	const [error, setError] = useState<string | null>(null);

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const data = new FormData(e.currentTarget);
		try {
			const accountId = await register.submit(data);
			if (accountId) {
				const account = newAccount(accountId, idbp, persistence);
				if (data.has("password")) {
					const token = account.client.addPasswordNeededListener(() => {
						account.client.usePassword("" + data.get("password"));
						account.client.removeEventListener(token);
					});
				}
				if (setAccounts) {
					setAccounts((accounts: any) => ({ ...accounts, [accountId]: account }));
				}
				navigate(href("/accounts/:accountId", { accountId }));
			} else {
				navigate("/login");
			}
		} catch (ex) {
			console.error(ex);
			setError("" + ex);
		}
	};

	const fields = form?.fields ?? [];

	return (
		<div className="min-h-screen flex items-center justify-center p-4">
			<div className="glass-panel rounded-2xl p-8 w-full max-w-sm shadow-2xl">
				<h1 className="text-xl font-semibold text-white mb-6">Create Account</h1>

				{error && (
					<div className="mb-4 px-4 py-3 rounded-xl bg-red-500/20 border border-red-500/30 text-red-300 text-sm">
						{error}
					</div>
				)}

				<form onSubmit={handleSubmit} className="space-y-3">
					{fields.map((field: any) => (
						<input
							key={field.name}
							name={field.name}
							type={field.type === "password" ? "password" : "text"}
							placeholder={field.label || field.name}
							required={field.required}
							className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white placeholder-white/40 text-sm focus:outline-none focus:border-primary transition-all"
						/>
					))}
					<button
						type="submit"
						className="w-full py-3 rounded-xl bg-primary hover:bg-primary-light text-white font-medium text-sm transition-colors"
					>
						Register
					</button>
				</form>
			</div>
		</div>
	);
};

const RegisterError = () => {
	const error = useAsyncError();
	return (
		<div className="min-h-screen flex items-center justify-center p-4">
			<div className="glass-panel rounded-2xl p-8 w-full max-w-sm">
				<p className="text-red-300 text-sm">Registration failed: {"" + error}</p>
			</div>
		</div>
	);
};

export const Register = () => {
	const [server] = useQueryState("with");

	const registerP = server
		? startRegistration(server)
		: Promise.resolve(null);

	return (
		<Suspense fallback={
			<div className="min-h-screen flex items-center justify-center">
				<div className="w-6 h-6 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
			</div>
		}>
			<Await resolve={registerP} errorElement={<RegisterError />}>
				<RegisterInner />
			</Await>
		</Suspense>
	);
};
